// src/features/tidsregistering/pages/BalanceManagementPage.jsx
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tooltip, CircularProgress, Alert,
  Collapse, TextField, InputAdornment,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Edit as EditIcon,
  Check as ConfirmIcon,
  Close as CancelIcon,
  Search as SearchIcon,
  PlaylistAdd as SeedIcon,
} from '@mui/icons-material';

import { useStaff } from '../../staff/hooks/useStaff';
import { useBalances } from '../hooks/useBalances';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import StaffMember from '../../../components/common/StaffMember';
import { balancesAPI } from '../services/balances';
import { BALANCE_LABELS } from '../constants/balances';

import PageHeader from '../../../components/common/PageHeader';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

function EntitlementCell({ balance, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(Number(balance.entitlement_hours)));
  const [saving, setSaving] = useState(false);
  const { showError } = useSnackbar();

  const handleConfirm = async () => {
    const hours = parseFloat(value);
    if (isNaN(hours) || hours < 0) {
      showError('Ugyldig timetal');
      return;
    }
    setSaving(true);
    try {
      const res = await balancesAPI.setEntitlement(balance.absence_balance_id, { entitlement_hours: hours });
      if (res.data) {
        onSaved(res.data);
        setEditing(false);
      }
    } catch {
      showError('Kunne ikke gemme');
    }
    setSaving(false);
  };

  const handleCancel = () => {
    setValue(String(Number(balance.entitlement_hours)));
    setEditing(false);
  };

  if (editing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <TextField
          size="small"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          type="number"
          inputProps={{ min: 0, step: 0.5 }}
          sx={{ width: 90, '& input': { py: 0.5, fontSize: 13 } }}
          autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter') handleConfirm(); if (e.key === 'Escape') handleCancel(); }}
        />
        <IconButton size="small" color="success" onClick={handleConfirm} disabled={saving}>
          {saving ? <CircularProgress size={14} /> : <ConfirmIcon fontSize="small" />}
        </IconButton>
        <IconButton size="small" onClick={handleCancel}>
          <CancelIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="body2">{Number(balance.entitlement_hours).toFixed(2)}t</Typography>
      <Tooltip title="Rediger rettighed">
        <IconButton size="small" onClick={() => setEditing(true)}>
          <EditIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function StaffBalanceRow({ member, onError }) {
  const [expanded, setExpanded] = useState(false);
  const [balances, setBalances] = useState(null); // null = not loaded
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const { showSuccess, showError } = useSnackbar();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await balancesAPI.listForStaff(member.staff_member_id);
      setBalances(res.data?.balances ?? []);
    } catch {
      showError('Kunne ikke hente saldi');
      setBalances([]);
    }
    setLoading(false);
  }, [member.staff_member_id, showError]);

  const handleToggle = () => {
    if (!expanded && balances === null) load();
    setExpanded((v) => !v);
  };

  const handleSeed = async (e) => {
    e.stopPropagation();
    setSeeding(true);
    try {
      await balancesAPI.seedPeriod(member.staff_member_id);
      await load();
      if (!expanded) setExpanded(true);
      showSuccess('Saldi initialiseret');
    } catch {
      showError('Kunne ikke initialisere saldi');
    }
    setSeeding(false);
  };

  const handleBalanceSaved = (updatedBalance) => {
    setBalances((prev) =>
      prev?.map((b) => b.absence_balance_id === updatedBalance.absence_balance_id ? updatedBalance : b) ?? prev
    );
  };

  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer' }} onClick={handleToggle}>
        <TableCell sx={{ width: 32, pr: 0 }}>
          <IconButton size="small">
            {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          <StaffMember staffMember={member} avatarSize={28} gap={1} nameVariant="body2" clickable={false} />
        </TableCell>
        <TableCell align="right">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
            {balances !== null && (
              <Typography variant="body2" color="text.secondary">
                {balances.length} saldi
              </Typography>
            )}
            <Tooltip title="Initialiser saldi for nuværende periode">
              <span>
                <IconButton size="small" onClick={handleSeed} disabled={seeding}>
                  {seeding ? <CircularProgress size={16} /> : <SeedIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={3} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
          <Collapse in={expanded} unmountOnExit>
            <Box sx={{ py: 1, pl: 6 }}>
              {loading ? (
                <CircularProgress size={20} />
              ) : balances?.length === 0 ? (
                <Typography variant="body2" color="text.disabled">Ingen saldi registreret</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Kode</TableCell>
                      <TableCell>Periode</TableCell>
                      <TableCell align="right">Rettighed</TableCell>
                      <TableCell align="right">Optjent</TableCell>
                      <TableCell align="right">Overført</TableCell>
                      <TableCell align="right">Brugt</TableCell>
                      <TableCell align="right">Tilbage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {balances?.map((b) => (
                      <TableRow key={b.absence_balance_id}>
                        <TableCell>
                          <Typography variant="body2">{BALANCE_LABELS[b.balance_code] ?? b.balance_code}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {b.period_start} – {b.period_end}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <EntitlementCell balance={b} onSaved={handleBalanceSaved} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{Number(b.accrued_hours).toFixed(2)}t</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{Number(b.carried_over_hours).toFixed(2)}t</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{Number(b.used_hours).toFixed(2)}t</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            color={Number(b.remaining_hours) < 0 ? 'error.main' : 'text.primary'}
                            fontWeight={600}
                          >
                            {Number(b.remaining_hours).toFixed(2)}t
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function BalanceManagementPage() {
  const { t } = useTranslation(['tidsregistering', 'common']);
  const { staff, fetchDepartmentStaff, loading: staffLoading, error: staffError } = useStaff();
  const { showError } = useSnackbar();
  const [search, setSearch] = useState('');

  useEffect(() => { fetchDepartmentStaff(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = (staff ?? []).filter((s) =>
    !search || s.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        mb={4}
        icon={AccountBalanceWalletIcon}
        title={t('common:navigation.tidsregistering_balances')}
        subtitle={t('tidsregistering:page_subtitles.balances')}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          size="small"
          placeholder="Søg medarbejder…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 240 }}
        />
      </Box>

      {staffError && <Alert severity="error">{staffError}</Alert>}

      <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 32 }} />
              <TableCell>Medarbejder</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {staffLoading && filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                  Ingen medarbejdere fundet
                </TableCell>
              </TableRow>
            ) : filtered.map((member) => (
              <StaffBalanceRow key={member.staff_member_id} member={member} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
